/**
 * Liste des campagnes — écran d'accueil du portail client (plan v3, phase 7).
 *
 * Une seule souscription (`listerCampagnesVue`), filtrage et tri côté client.
 * Les filtres (recherche, statut, type, template) vivent dans l'URL : coller le
 * lien reproduit la vue, et le retour navigateur n'a plus besoin de
 * `sessionStorage`.
 */
import { AjouterCandidatsDialog } from "@repo/campagne";
import {
  Button,
  Card,
  CardContent,
  DataTable,
  PageContainer,
  PageHeader,
  useFiltresColonnesUrl,
} from "@repo/ui";
import { useQuery } from "convex/react";
import { Plus, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import {
  colonnesCampagnes,
  ETAT_ARCHIVE,
  FACETTES_CAMPAGNES,
  RECHERCHE_CAMPAGNES,
} from "./colonnesCampagnes";

/** Références stables : recréées à chaque rendu, elles feraient boucler les mémos. */
const CHAMPS_FILTRES = {
  q: { colonne: RECHERCHE_CAMPAGNES.colonne },
  statut: { colonne: "statut", multi: true },
  epreuves: { colonne: "applications", multi: true },
  template: { colonne: "templates", multi: true },
  archive: { colonne: "archive", multi: true },
} as const;
/**
 * Sans filtre d'archivage dans l'URL, la liste ne montre que les campagnes
 * actives ; les archivées se retrouvent via la facette « Archivage ». Injecté
 * ici plutôt que dans l'URL, pour qu'une vue par défaut garde une adresse
 * propre — même mécanique que le portail admin.
 */
const FILTRE_ARCHIVE_DEFAUT = {
  id: "archive",
  value: [ETAT_ARCHIVE.ACTIVES],
};
const BARRE_FILTRES = {
  recherche: RECHERCHE_CAMPAGNES,
  facettes: FACETTES_CAMPAGNES,
};

export function CampagneList() {
  const navigate = useNavigate();
  // Les contrôles sont rendus par la `FilterBar` de la table : mêmes segments,
  // mêmes compteurs de facette que partout ailleurs.
  const [filtres, setFiltres] = useFiltresColonnesUrl(CHAMPS_FILTRES);
  const [addCandidatsCampagne, setAddCandidatsCampagne] = useState<{
    id: Id<"campagnes">;
    nom: string;
    anonyme?: boolean;
  } | null>(null);

  const vue = useQuery(api.campagnes.listerCampagnesVue, {});

  // Filtre par défaut seulement s'il y a des archivées à masquer : sans elles,
  // il ferait passer une liste vide pour une liste « filtrée » (« Aucune
  // campagne pour ces filtres » au lieu de l'invitation à en créer une).
  const aArchivees = (vue?.lignes ?? []).some((l) => l.archivedAt);
  const filtresEffectifs = useMemo(
    () =>
      !aArchivees || filtres.some((f) => f.id === "archive")
        ? filtres
        : [...filtres, FILTRE_ARCHIVE_DEFAUT],
    [filtres, aArchivees],
  );
  // Le compteur suit ce que la liste montre par défaut : les actives.
  const nbActives = (vue?.lignes ?? []).filter((l) => !l.archivedAt).length;

  const colonnes = useMemo(
    () =>
      colonnesCampagnes({
        onAjouterCandidats: (c) =>
          setAddCandidatsCampagne({
            id: c._id,
            nom: c.nom,
            anonyme: c.anonyme,
          }),
        onEnvoyerLiens: (c) =>
          void navigate(`/campagnes/${c._id}?openEmailModal=true`),
      }),
    [navigate],
  );

  return (
    <div className="min-h-screen flex flex-col items-start">
      <PageContainer>
        <PageHeader
          titre="Campagnes"
          sousTitre={
            vue
              ? `${nbActives} campagne${nbActives > 1 ? "s" : ""} active${nbActives > 1 ? "s" : ""}`
              : "Chargement…"
          }
          actions={
            <Link to="/campagnes/creer">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Créer une campagne
              </Button>
            </Link>
          }
        />

        <DataTable
          colonnes={colonnes}
          donnees={vue?.lignes}
          cleColonnes="campagnes"
          hrefLigne={(ligne) => `/campagnes/${ligne._id}`}
          barreFiltres={BARRE_FILTRES}
          filtres={filtresEffectifs}
          onFiltresChange={setFiltres}
          vide={{
            titre: "Aucune campagne",
            description: "Créez une campagne pour démarrer des évaluations.",
            action: (
              <Link to="/campagnes/creer">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Créer une campagne
                </Button>
              </Link>
            ),
          }}
          videFiltre={{
            titre: "Aucune campagne pour ces filtres",
            action: (
              // Explicite : vider les filtres ramènerait le défaut « Actives ».
              <Button
                variant="outline"
                onClick={() =>
                  setFiltres([
                    { id: "archive", value: [ETAT_ARCHIVE.ACTIVES, ETAT_ARCHIVE.ARCHIVEES] },
                  ])
                }
              >
                Voir toutes les campagnes
              </Button>
            ),
          }}
        />

        {/* Pseudo-campagne « Hors campagne » (§0.3) : hors du tableau, parce
            qu'elle n'est pas une campagne — ni triable, ni filtrable avec les
            autres. Ne se rend pas si le compte est nul. */}
        {!!vue?.nbHorsCampagne && (
          <Card className="mt-4">
            <CardContent className="p-0">
              <Link
                to="/campagnes/hors-campagne"
                className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-muted/50"
              >
                <div>
                  <div className="font-medium">Hors campagne</div>
                  <div className="text-sm text-muted-foreground">
                    Parcours créés sans campagne
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="data">{vue.nbHorsCampagne}</span>
                </div>
              </Link>
            </CardContent>
          </Card>
        )}
      </PageContainer>

      {addCandidatsCampagne && (
        <AjouterCandidatsDialog
          isOpen={true}
          onClose={() => setAddCandidatsCampagne(null)}
          campagneId={addCandidatsCampagne.id}
          campagneNom={addCandidatsCampagne.nom}
          anonyme={addCandidatsCampagne.anonyme}
        />
      )}
    </div>
  );
}
